import React, { useEffect, useState } from 'react';
import { Calculator, TrendingUp, Calendar, Percent } from 'lucide-react';
export const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(100000);
  const [months, setMonths] = useState(24);
  const [interestRate, setInterestRate] = useState(3.5);
  const [paymentMethod, setPaymentMethod] = useState<'reducing' | 'flat'>('reducing');
  const calculateLoan = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    let monthlyPayment = 0;
    let totalInterest = 0;
    let totalAmount = 0;
    if (paymentMethod === 'reducing') {
      // Reducing balance method
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
      totalAmount = monthlyPayment * months;
      totalInterest = totalAmount - principal;
    } else {
      // Flat rate method
      totalInterest = principal * (interestRate / 100) * (months / 12);
      totalAmount = principal + totalInterest;
      monthlyPayment = totalAmount / months;
    }
    return {
      monthlyPayment: Math.round(monthlyPayment),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount)
    };
  };
  const results = calculateLoan();
  return <section className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-963">
      <div className="container" data-id="element-964">
        <div className="text-center mb-12" data-id="element-965">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-5 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-966">
            <Calculator size={18} data-id="element-967" />
            <span data-id="element-968">Financial Planning Tool</span>
          </div>
          <h2 className="section-title" data-id="element-969">Loan Calculator</h2>
          <p className="section-subtitle" data-id="element-970">
            Calculate your monthly payments and plan your financing with our
            interactive calculator
          </p>
        </div>

        <div className="max-w-5xl mx-auto" data-id="element-971">
          <div className="grid lg:grid-cols-2 gap-8" data-id="element-972">
            {/* Calculator Inputs */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-gray-200" data-id="element-973">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-3" data-id="element-974">
                <Calculator size={28} className="text-primary-600" data-id="element-975" />
                Calculate Your Loan
              </h3>

              <div className="space-y-6" data-id="element-976">
                {/* Loan Amount */}
                <div data-id="element-977">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-978">
                    Loan Amount (KSh)
                  </label>
                  <input type="number" value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="50000" max="10000000" step="10000" data-id="element-979" />
                  <input type="range" value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))} min="50000" max="10000000" step="10000" className="w-full mt-3" data-id="element-980" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-981">
                    <span data-id="element-982">KSh 50K</span>
                    <span data-id="element-983">KSh 10M</span>
                  </div>
                </div>

                {/* Number of Months */}
                <div data-id="element-984">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-985">
                    Number of Months
                  </label>
                  <input type="number" value={months} onChange={e => setMonths(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="6" max="60" data-id="element-986" />
                  <input type="range" value={months} onChange={e => setMonths(Number(e.target.value))} min="6" max="60" className="w-full mt-3" data-id="element-987" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-988">
                    <span data-id="element-989">6 months</span>
                    <span data-id="element-990">60 months</span>
                  </div>
                </div>

                {/* Annual Interest Rate */}
                <div data-id="element-991">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-992">
                    Annual Interest Rate (%)
                  </label>
                  <input type="number" value={interestRate} onChange={e => setInterestRate(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="1" max="30" step="0.1" data-id="element-993" />
                  <input type="range" value={interestRate} onChange={e => setInterestRate(Number(e.target.value))} min="1" max="30" step="0.1" className="w-full mt-3" data-id="element-994" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-995">
                    <span data-id="element-996">1%</span>
                    <span data-id="element-997">30%</span>
                  </div>
                </div>

                {/* Payment Method */}
                <div data-id="element-998">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-999">
                    Payment Method
                  </label>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as 'reducing' | 'flat')} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" data-id="element-1000">
                    <option value="reducing" data-id="element-1001">Reducing Balance</option>
                    <option value="flat" data-id="element-1002">Flat Rate</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6" data-id="element-1003">
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 shadow-xl text-white" data-id="element-1004">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-1005">
                  <TrendingUp size={28} data-id="element-1006" />
                  Your Results
                </h3>

                <div className="space-y-6" data-id="element-1007">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6" data-id="element-1008">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1009">
                      <Calendar size={24} data-id="element-1010" />
                      <p className="text-sm text-primary-100" data-id="element-1011">
                        Monthly Payment
                      </p>
                    </div>
                    <p className="text-4xl font-bold" data-id="element-1012">
                      KSh {results.monthlyPayment.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6" data-id="element-1013">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1014">
                      <Percent size={24} data-id="element-1015" />
                      <p className="text-sm text-primary-100" data-id="element-1016">Total Interest</p>
                    </div>
                    <p className="text-3xl font-bold" data-id="element-1017">
                      KSh {results.totalInterest.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6" data-id="element-1018">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1019">
                      <TrendingUp size={24} data-id="element-1020" />
                      <p className="text-sm text-primary-100" data-id="element-1021">
                        Total Amount Payable
                      </p>
                    </div>
                    <p className="text-3xl font-bold" data-id="element-1022">
                      KSh {results.totalAmount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-primary-200" data-id="element-1023">
                <h4 className="font-bold text-gray-900 mb-3" data-id="element-1024">Loan Summary</h4>
                <div className="space-y-2 text-sm" data-id="element-1025">
                  <div className="flex justify-between" data-id="element-1026">
                    <span className="text-gray-600" data-id="element-1027">Principal Amount:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1028">
                      KSh {loanAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1029">
                    <span className="text-gray-600" data-id="element-1030">Loan Period:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1031">
                      {months} months ({(months / 12).toFixed(1)} years)
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1032">
                    <span className="text-gray-600" data-id="element-1033">Interest Rate:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1034">
                      {interestRate}% p.a.
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1035">
                    <span className="text-gray-600" data-id="element-1036">Method:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1037">
                      {paymentMethod === 'reducing' ? 'Reducing Balance' : 'Flat Rate'}
                    </span>
                  </div>
                </div>
              </div>

              <a href="/apply" className="block w-full text-center py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1038">
                Apply for This Loan
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};